import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TutorialService } from 'src/app/core/tutorial/tutorial.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { VideoDialogComponent } from '../video-dialog/video-dialog.component';
import { UserService } from 'src/app/core/services/user.service';
import { GroupService } from 'src/app/core/services/group.service';
import { SupportService } from 'src/app/core/services/support.service';
import { ChatService } from 'src/app/chat/chat.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ForceRefreshService } from 'src/app/core/services/force-refresh.service';

@Component({
  selector: 'app-help-index-dialog',
  templateUrl: './help-index-dialog.component.html',
  styleUrls: ['./help-index-dialog.component.scss']
})
export class HelpIndexDialogComponent implements OnInit {
  @ViewChild('slideContainer') slideContainer?: ElementRef<HTMLElement>;

  user: any;
  group: any;
  isDoctor: boolean = false;
  isAdmin: boolean = false;
  tutorials: any = [];
  openingWhatsAppSupport: boolean = false;
  openingInAppSupport = false;
  refreshing = false;

  constructor(
    private tutorialService: TutorialService,
    public dialogRef: MatDialogRef<HelpIndexDialogComponent>,
    private dialog: MatDialog,
    private userService: UserService,
    private groupService: GroupService,
    private supportService: SupportService,
    private chatService: ChatService,
    private snack: MatSnackBar,
    private forceRefreshService: ForceRefreshService,
  ) { }

  /** null until known; false when chat is off here (the button is hidden, WhatsApp remains) */
  readonly chatEnabled$ = this.chatService.enabled$;

  ngOnInit() {
    this.userService.userProfile$
      .subscribe(user => { this.user = user; this.isDoctor = this.user.userType.toLowerCase() === 'doctor'; });

    this.groupService.selectedGroup$
      .subscribe(group => { this.group = group; this.isAdmin = this.group.userRole.toLowerCase() === 'admin' });

    // Ensure dialog always shows unless manually closed
    if (!localStorage.getItem('helpDialogDismissed')) {
      this.dialogRef.afterClosed().subscribe(() => {
        localStorage.removeItem('helpDialogDismissed'); // Reset on close
      });
    }

    if (!this.isAdmin && !this.isDoctor) {
      this.tutorials = [
        { label: 'Adding Patients', url: 'https://www.youtube.com/watch?v=IUjoxO2C1zI' },
        { label: 'Appointments & Calendar', url: 'https://www.youtube.com/watch?v=oVArAPJCjww' },
        { label: 'Admitting and Discharging', url: 'https://www.youtube.com/watch?v=1N690PSxChg' },
      ]
    } else if (this.isDoctor || this.isAdmin) {
      this.tutorials = [
        { label: 'Custom Note Types', url: 'https://www.youtube.com/watch?v=84PthVIrnPA' },
        { label: 'Custom Appointment Types', url: 'https://www.youtube.com/watch?v=kg42H2ZpgFY' },
        { label: 'Procedure Related Consent Forms', url: 'https://www.youtube.com/watch?v=Z4ET_L6iTHA' },
        { label: 'Adding Patients', url: 'https://www.youtube.com/watch?v=IUjoxO2C1zI' },
        { label: 'Appointments & Calendar', url: 'https://www.youtube.com/watch?v=oVArAPJCjww' },
        { label: 'Admitting and Discharging', url: 'https://www.youtube.com/watch?v=1N690PSxChg' },
      ]
    } else {
      this.tutorials = [
        { label: 'Adding Patients', url: 'https://www.youtube.com/watch?v=IUjoxO2C1zI' },
        { label: 'Appointments & Calendar', url: 'https://www.youtube.com/watch?v=oVArAPJCjww' },
        { label: 'Admitting and Discharging', url: 'https://www.youtube.com/watch?v=1N690PSxChg' },
      ]
    }

    //this.dialogRef.updateSize('25vw', 'auto');
  }

  restartTutorial() {
    this.tutorialService.resetTutorialToDefaults().subscribe({
      next: () => {
        console.log("Tutorial progress reset successfully.");
        this.dialogRef.close();
        window.location.reload();
      },
      error: (err: Error) => {
        console.error('Error resetting tutorial', err);
      }
    });
  }

  /** Same as Ctrl+Shift+R on every device: loads the latest version of the app. */
  async forceRefresh(): Promise<void> {
    this.refreshing = true;
    this.dialogRef.close();
    await this.forceRefreshService.forceRefresh();
  }

  closeDialog() {
    localStorage.setItem('helpDialogDismissed', 'true'); // Remember dismissal
    this.dialogRef.close();
  }

  /** In-app support (M7): the chat window opens on this user's conversation with MedicAI Support. */
  async openInAppSupport(): Promise<void> {
    if (!this.group || this.openingInAppSupport) return;
    this.openingInAppSupport = true;
    try {
      await this.chatService.openSupportChat(this.group.id);
      this.dialogRef.close();
    } catch {
      this.snack.open('Support chat could not be opened just now. Try WhatsApp Support instead.', 'OK', { duration: 5000 });
    } finally {
      this.openingInAppSupport = false;
    }
  }

  openWhatsAppSupport(): void {
    if (!this.user || !this.group || this.openingWhatsAppSupport) {
      return;
    }
    this.openingWhatsAppSupport = true;
    this.supportService.openWhatsAppSupport(this.user, this.group).subscribe({
      next: () => {
        this.openingWhatsAppSupport = false;
      },
      error: () => {
        this.openingWhatsAppSupport = false;
        console.error('Failed to open WhatsApp support');
      }
    });
  }

  openVideoDialog(videoUrl: string): void {
    this.tutorialService.closeTutorial();

    const dialogRef = this.dialog.open(VideoDialogComponent, {
      data: { videoUrl },
      width: '80vw',
      height: '80vh',
      panelClass: 'fullscreen-video-dialog',
    });

    dialogRef.afterOpened().subscribe(() => {
      this.setZIndex(999);
    });

    dialogRef.afterClosed().subscribe(() => {
      this.setZIndex(99999);
    });
  }


  private setZIndex(value: number): void {
    const container = this.slideContainer?.nativeElement;
    if (!container) return;

    container.style.zIndex = value.toString();
  }
}
